// noinspection ES6UnusedImports

import { createTheme as createMuiTheme, Theme } from '@mui/material/styles';
import {} from '@mui/x-date-pickers/themeAugmentation';
import { CbridgePalette } from './theme.constant';
import { Brand, BRAND_PALETTE } from './brand.constant';

import { MuiButton } from './MuiButton';
import { MuiDataGrid } from './MuiDataGrid';
import { MuiInputBase } from './MuiInputBase';
import { MuiChip } from './MuiChip';
import { MuiSwitch } from './MuiSwitch';
import { typography } from './typography';

export function createTheme(compact?: boolean, brand: Brand = Brand.CBridge): Theme {
  const brandPalette = BRAND_PALETTE[brand];

  return createMuiTheme({
    typography: {
      ...(compact ? { fontSize: 12 } : {}),
      ...typography,
    },
    palette: {
      mode: 'dark',
      primary: CbridgePalette.primary,
      text: CbridgePalette.text,
      error: {
        main: CbridgePalette.error.main,
        light: CbridgePalette.error.light,
      },
      secondary: {
        main: brandPalette.accent,
      },
      brand: brandPalette,
      warning: {
        main: CbridgePalette.warning,
      },
      success: {
        main: CbridgePalette.success,
      },
      info: {
        main: CbridgePalette.info,
      },
      action: {
        active: CbridgePalette.action.active,
      },
      background: {
        default: CbridgePalette.background.main,
        paper: CbridgePalette.background.sideBar,
      },
      DataGrid: {
        headerBg: CbridgePalette.background.h1,
        bg: 'transparent',
      },
      divider: CbridgePalette.divider,
    },
    cssVariables: {
      colorSchemeSelector: 'data-toolpad-color-scheme',
    },
    shape: {
      borderRadius: 8,
    },
    colorSchemes: { dark: true },
    components: {
      MuiCssBaseline: {
        styleOverrides: (theme) => ({
          html: {
            height: '100%',
            overflow: 'auto',
          },
          body: {
            height: '100%',
            margin: 0,
            overflow: 'auto',
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              position: 'absolute',
              backgroundColor: 'transparent',
              width: 8,
              height: 8,
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              width: 6,
              borderRadius: 100,
              backgroundColor: 'transparent',
              transition: 'background-color 0.3s ease',
            },
            '&:hover::-webkit-scrollbar-thumb, & *:hover::-webkit-scrollbar-thumb': {
              backgroundColor: CbridgePalette.scrollbar.thumb,
            },
            '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
              backgroundColor: CbridgePalette.scrollbar.thumb,
            },
            '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
              backgroundColor: CbridgePalette.scrollbar.thumb,
            },
            '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
              backgroundColor: CbridgePalette.scrollbar.thumb,
            },
            '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
              backgroundColor: 'transparent',
            },
            '.simplebar-scrollbar::before': {
              backgroundColor: CbridgePalette.scrollbar.thumb,
            },
            '.simplebar-scrollbar.simplebar-visible:before': {
              opacity: 1,
            },
          },
          '#root': {
            height: '100%',
          },
          ':root': {
            '--cbridge-palette-background-main': CbridgePalette.background.main,
            '--cbridge-palette-background-sideBar': CbridgePalette.background.sideBar,
            '--cbridge-palette-background-tableRow': CbridgePalette.background.tableRow,
            '--cbridge-palette-background-h1': CbridgePalette.background.h1,
            '--cbridge-palette-background-h2': CbridgePalette.background.h2,
            '--cbridge-font-size-xs': '10px',
            '--cbridge-font-size-sm': '12px',
            '--cbridge-font-size-md': '14px',
          },
          '.CBridge-config-panel': {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--cbridge-palette-background-sideBar)',
            color: theme.palette.text.secondary,
            overflow: 'hidden',
            position: 'relative',
          },
          '.CBridge-config-panel .CBridge-config-panel-nav': {
            borderRight: `1px solid ${theme.palette.divider}`,
          },
          '.CBridge-config-panel .CBridge-config-panel-details': {
            borderLeft: `1px solid ${theme.palette.divider}`,
          },
          '.CBridge-config-panel-main': {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
            minWidth: 0,
            padding: theme.spacing(0, 2),
          },
          '.CBridge-config-panel-resizer': {
            position: 'relative',
            width: 2,
            height: '100%',
            cursor: 'col-resize',
          },
        }),
      },
      MuiAlert: {
        styleOverrides: {
          message: {
            fontSize: 14,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          popper: {
            '& .MuiAutocomplete-groupLabel': {
              backgroundColor: CbridgePalette.background.h1,
              lineHeight: '36px',
            },
          },
          popupIndicator: {
            variants: [
              {
                props: { disabled: true },
                style: {
                  display: 'none',
                },
              },
            ],
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            background: CbridgePalette.background.sideBar,
            border: 'none',
            borderBottom: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
            zIndex: theme.zIndex.drawer + 1,
            '& .MuiButtonBase-root': {
              fontSize: 14,
            },
          }),
        },
      },
      MuiButton,
      MuiButtonGroup: {
        styleOverrides: {
          root: {
            '& .MuiButtonGroup-middleButton, & .MuiButtonGroup-firstButton': {
              borderColor: CbridgePalette.divider,
            },
          },
        },
      },
      MuiCard: {
        defaultProps: {
          square: true,
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            borderRadius: 'unset',
          },
        },
      },
      MuiDataGrid,
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: CbridgePalette.background.h2,
            backgroundImage: 'unset',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderWidth: 0,
            borderBottomWidth: 1,
            borderStyle: 'solid',
            borderColor: (theme.vars ?? theme).palette.divider,
          }),
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(2, 3),
            marginTop: theme.spacing(2),
          }),
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(2),
          }),
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme }) => ({
            color: theme.palette.text.secondary,
          }),
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.secondary,
          }),
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: 'inherit',

            '&.Mui-selected, &:hover': {
              backgroundColor: CbridgePalette.background.selection,
              color: theme.palette.text.primary,
            },
          }),
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'unset !important',
          },

          elevation0: {
            backgroundColor: CbridgePalette.background.tableRow,
          },
          elevation1: {
            backgroundColor: CbridgePalette.background.sideBar,
          },
          elevation3: {
            backgroundColor: CbridgePalette.background.h2,
          },
          elevation4: {
            backgroundColor: CbridgePalette.background.h1,
          },
        },
      },
      MuiPickersTextField: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'capitalize',
          },
        },
      },
      MuiToolbar: {
        defaultProps: {
          variant: 'dense',
        },
      },
      MuiTextField: {
        defaultProps: {
          fullWidth: true,
          size: 'small',
          variant: 'standard',
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            background: CbridgePalette.background.tooltips,
            padding: theme.spacing(0.75, 1),
            fontSize: '10px',
            whiteSpace: 'pre-line',
          }),
          arrow: {
            color: CbridgePalette.background.tooltips,
          },
        },
      },
      MuiInputBase,
      MuiInput: {
        // Attribute 'data-form-view-mode' used in FormProvider
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.primary,
            '&.Mui-disabled': {
              color: theme.palette.text.secondary,
            },
            '[data-form-edit-mode] &.Mui-disabled': {
              color: theme.palette.text.disabled,
            },
          }),
          input: ({ theme }) => ({
            '&.Mui-disabled': {
              WebkitTextFillColor: theme.palette.text.secondary,
            },
            '[data-form-edit-mode] &.Mui-disabled': {
              WebkitTextFillColor: theme.palette.text.disabled,
            },
          }),
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          asterisk: ({ theme }) => ({
            color: theme.palette.error.main,
          }),
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            variants: [
              {
                props: { disabled: true },
                style: {
                  display: 'none',
                },
              },
            ],
          },
        },
      },
      MuiPickersInputBase: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
              borderWidth: 1,
            },

            // Changed fields (see FieldRevert) get a yellow underline and picker icon;
            // other adornments (the clear "x") keep their colour.
            // Error styling still wins via :not(.Mui-error).
            '[data-field-dirty="true"] &:not(.Mui-error)': {
              '&::before, &::after': {
                borderBottomColor: theme.palette.warning.main,
              },
              '&:hover:not(.Mui-disabled)::before': {
                borderBottomColor: theme.palette.warning.main,
              },
              '& [data-mui-picker-open-button] .MuiSvgIcon-root': {
                color: theme.palette.warning.main,
              },
            },
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 1,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: ({ theme }) => ({
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.brand.accent,
            },
          }),
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            color: CbridgePalette.text.primary,
            backgroundColor: CbridgePalette.background.h1,
          },
        },
      },
      MuiCardActions: {
        styleOverrides: {
          root: {
            backgroundColor: CbridgePalette.background.h1,
          },
        },
      },
      MuiChip,
      MuiSwitch,
    },
  });
}
